import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle, Plus, Trash2, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MatchResult, getConfidenceLevel } from './matchingUtils';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface Column {
  key: string;
  label: string;
  required: boolean;
  readOnly?: boolean;
  width?: string;
  sortable?: boolean;
  minWidth?: number;
}

interface GridRowData {
  [key: string]: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface SpreadsheetGridFixedProps {
  columns: Column[];
  data: GridRowData[];
  onChange: (data: GridRowData[]) => void;
  validationResults: Record<number, ValidationResult>;
  matchResults?: Record<number, MatchResult>;
  currentUserName: string;
  kolDbData?: any[];
  kolTrackerData?: any[];
  onManualMatch?: (rowIndex: number, kolDbId: string, extractedHandler: string) => void;
  onDeleteRow?: (rowIndex: number) => void;
  selectedTable?: string;
  onValidationTrigger?: (rowIndex: number) => void;
}

export function SpreadsheetGridFixed({
  columns,
  data,
  onChange,
  validationResults,
  matchResults = {},
  currentUserName,
  kolDbData = [],
  kolTrackerData = [],
  onManualMatch,
  onDeleteRow,
  selectedTable,
  onValidationTrigger
}: SpreadsheetGridFixedProps) {
  const [focusedCell, setFocusedCell] = useState<{ row: number; col: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [isPasting, setIsPasting] = useState(false);
  const { user } = useAuth();
  const [userAccessLevel, setUserAccessLevel] = useState<string>('limited');
  
  // Refs for scroll synchronization
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  // FIXED: Enhanced operation tracking to prevent validation interference
  const [operationsInProgress, setOperationsInProgress] = useState<Set<number>>(new Set());
  const [lastValidationTimestamp, setLastValidationTimestamp] = useState<Record<number, number>>({});

  // Fetch user access level
  useEffect(() => {
    const fetchUserAccess = async () => {
      if (!user?.email) return;
      
      try {
        const { data, error } = await supabase
          .from('kol_pic')
          .select('access')
          .eq('email', user.email)
          .single();
        
        if (error) {
          console.error("Error fetching user access:", error);
          return;
        }
        
        setUserAccessLevel(data?.access || 'limited');
      } catch (error) {
        console.error("Error in fetchUserAccess:", error);
      }
    };

    fetchUserAccess();
  }, [user?.email]);

  // Enhanced debug logging for match results tracking
  useEffect(() => {
    console.log('🔍 SpreadsheetGridFixed - Debug State Update:');
    console.log('  - KOL DB Data:', kolDbData?.length || 0, 'records');
    console.log('  - KOL Tracker Data:', kolTrackerData?.length || 0, 'records');
    console.log('  - Match Results:', Object.keys(matchResults).length, 'results');
    console.log('  - Match Results Detail:', matchResults);
    console.log('  - Selected Table:', selectedTable);
    console.log('  - Current Data Rows:', data.length);
  }, [kolDbData, kolTrackerData, matchResults, selectedTable, data]);

  // KOL name correction mapping - ENHANCED with more corrections
  const nameCorrections = useMemo(() => {
    return {
      'Alex MVPs': 'Alex MVP',
      'Cryptox Slivy': 'Crypto Slivy',
      'Crypto Slay': 'CryptoSlay',
      'CryptX Slivy': 'Crypto Slivy',
      'Alex MVPS': 'Alex MVP',
      // Add more corrections as needed
    };
  }, []);

  // Function to get corrected name
  const getCorrectedName = useCallback((originalName: string): { correctedName: string; hasCorrection: boolean } => {
    const corrected = nameCorrections[originalName as keyof typeof nameCorrections];
    return {
      correctedName: corrected || originalName,
      hasCorrection: !!corrected
    };
  }, [nameCorrections]);

  // Smart date formatting function
  const formatSmartDate = useCallback((dateInput: string): string => {
    if (!dateInput || dateInput.trim() === '') return '';
    
    const cleanInput = dateInput.trim();
    const currentYear = new Date().getFullYear();
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleanInput)) {
      return cleanInput;
    }
    
    // Handle MM-DD format (assume current year)
    if (/^\d{1,2}-\d{1,2}$/.test(cleanInput)) {
      const [month, day] = cleanInput.split('-');
      return `${currentYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Handle DD/MM format (assume current year)
    if (/^\d{1,2}\/\d{1,2}$/.test(cleanInput)) {
      const [day, month] = cleanInput.split('/');
      return `${currentYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Handle other date formats and try to parse
    try {
      const date = new Date(cleanInput);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      // If parsing fails, return original input
    }
    
    return cleanInput;
  }, []);

  // Smart deliverable type mapping
  const mapDeliverableType = useCallback((type: string): string => {
    if (!type) return '';
    
    const lowerType = type.toLowerCase().trim();
    
    // Twitter mappings
    if (lowerType.includes('tweet') || lowerType === 'twitter tweet') {
      return 'Twitter - Tweet';
    }
    if (lowerType.includes('qt') || lowerType.includes('quote') || lowerType === 'twitter qt') {
      return 'Twitter - QT';
    }
    if (lowerType.includes('thread') || lowerType.includes('long post') || lowerType === 'twitter thread') {
      return 'Twitter - Thread/long post';
    }
    
    // Telegram mappings
    if (lowerType.includes('tg') || lowerType.includes('telegram') || lowerType === 'tg post') {
      return 'Telegram - post';
    }
    
    // Other mappings
    if (lowerType.includes('blog') || lowerType.includes('news')) {
      return 'Blogs or News';
    }
    if (lowerType.includes('youtube') || lowerType.includes('integration')) {
      return 'Youtube - integration';
    }
    if (lowerType.includes('naver') || lowerType.includes('blog')) {
      return 'Naver - Blog';
    }
    
    // Return original if no mapping found
    return type;
  }, []);

  // Manual validation trigger - properly calls validation when triggered manually
  const triggerValidationForRow = useCallback((rowIndex: number) => {
    if (onValidationTrigger) {
      console.log(`🔄 [Row ${rowIndex}] Manual validation triggered`);
      onValidationTrigger(rowIndex);
    } else {
      console.log(`⚠️ [Row ${rowIndex}] No validation trigger function available`);
    }
  }, [onValidationTrigger]);

  // Scroll synchronization effect
  useEffect(() => {
    const handleHeaderScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      if (bodyScrollRef.current && target.scrollLeft !== bodyScrollRef.current.scrollLeft) {
        bodyScrollRef.current.scrollLeft = target.scrollLeft;
      }
    };

    const handleBodyScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      if (headerScrollRef.current && target.scrollLeft !== headerScrollRef.current.scrollLeft) {
        headerScrollRef.current.scrollLeft = target.scrollLeft;
      }
    };

    const headerEl = headerScrollRef.current;
    const bodyEl = bodyScrollRef.current;

    if (headerEl && bodyEl) {
      headerEl.addEventListener('scroll', handleHeaderScroll);
      bodyEl.addEventListener('scroll', handleBodyScroll);

      return () => {
        headerEl.removeEventListener('scroll', handleHeaderScroll);
        bodyEl.removeEventListener('scroll', handleBodyScroll);
      };
    }
  }, []);

  // Enhanced responsive column width calculation - UPDATED for more compact status
  const columnWidths = useMemo(() => {
    const deleteRowWidth = 80;
    const statusWidth = 100; // Reduced from 120 for more compact status
    
    // Get container width with fallback for mobile
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const isMobile = screenWidth < 768;
    const isTablet = screenWidth >= 768 && screenWidth < 1024;
    
    const containerWidth = isMobile ? screenWidth - 40 : 
                          isTablet ? screenWidth - 80 : 
                          screenWidth - 120;
    
    const availableWidth = containerWidth - deleteRowWidth - statusWidth;
    const dataColumnsCount = columns.length;
    
    // Filter out action columns that shouldn't exist
    const actualColumns = columns.filter(col => col.key !== 'actions' && col.key !== 'correction_status');
    const actualDataColumnsCount = actualColumns.length;
    
    // Base width calculation with mobile considerations
    const baseWidth = Math.max(120, Math.floor(availableWidth / actualDataColumnsCount));
    
    const widthMap: Record<string, number> = {};
    
    actualColumns.forEach(col => {
      let width: number;
      
      if (isMobile) {
        // Mobile-optimized widths
        switch (col.key) {
          case 'creator_name':
          case 'name':
            width = Math.max(150, baseWidth * 1.2);
            break;
          case 'post_link':
            width = Math.max(200, baseWidth * 1.3);
            break;
          case 'matched_creator':
            width = Math.max(220, baseWidth * 1.4);
            break;
          case 'type':
            width = Math.max(140, baseWidth);
            break;
          case 'posting_date':
            width = Math.max(120, baseWidth * 0.9);
            break;
          case 'content_views':
            width = Math.max(100, baseWidth * 0.8);
            break;
          case 'pic':
            width = Math.max(120, baseWidth * 0.9);
            break;
          default:
            width = Math.max(130, baseWidth);
        }
      } else {
        // Desktop widths
        switch (col.key) {
          case 'creator_name':
          case 'name':
            width = Math.max(220, baseWidth * 1.3);
            break;
          case 'post_link':
            width = Math.max(280, baseWidth * 1.5);
            break;
          case 'matched_creator':
            width = Math.max(320, baseWidth * 1.6);
            break;
          case 'type':
            width = Math.max(200, baseWidth * 1.2);
            break;
          case 'posting_date':
            width = Math.max(160, baseWidth * 1.0);
            break;
          case 'content_views':
            width = Math.max(140, baseWidth * 0.9);
            break;
          case 'pic':
            width = Math.max(180, baseWidth * 1.1);
            break;
          default:
            width = Math.max(170, baseWidth * 1.1);
        }
      }
      
      widthMap[col.key] = width;
    });

    return widthMap;
  }, [columns]);

  // Enhanced sorting functionality
  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      // Handle numeric sorting for content_views
      if (sortConfig.key === 'content_views') {
        const aNum = parseFloat(aValue) || 0;
        const bNum = parseFloat(bValue) || 0;
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle date sorting
      if (sortConfig.key === 'posting_date') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return sortConfig.direction === 'asc' 
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }

      // Handle string sorting
      const comparison = aValue.localeCompare(bValue);
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  const handleSort = useCallback((columnKey: string) => {
    if (columnKey === 'correction_status') return;

    setSortConfig(current => {
      if (current?.key === columnKey) {
        return current.direction === 'asc' 
          ? { key: columnKey, direction: 'desc' }
          : null;
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, []);

  // FIXED: Enhanced cell change handler with better operation isolation
  const handleCellChange = useCallback((rowIndex: number, columnKey: string, value: string) => {
    console.log(`📝 [Row ${rowIndex}] Cell change starting: ${columnKey} = "${value}"`);
    
    // Mark this row as having an operation in progress
    setOperationsInProgress(prev => new Set(prev).add(rowIndex));
    
    const newData = [...data];
    if (!newData[rowIndex]) {
      newData[rowIndex] = {};
    }
    
    let processedValue = value;
    
    // Apply smart date formatting for date fields
    if (columnKey === 'posting_date') {
      processedValue = formatSmartDate(value);
    }
    
    // Apply deliverable type mapping for type field
    if (columnKey === 'type') {
      processedValue = mapDeliverableType(value);
    }
    
    // ENHANCED: Apply name correction for name field - Store original value in a separate field
    if (columnKey === 'name') {
      const { correctedName, hasCorrection } = getCorrectedName(value);
      // Store the original value and the corrected value
      newData[rowIndex]['_original_name'] = hasCorrection ? value : '';  // Store original only if corrected
      processedValue = correctedName; // Store the corrected name
      
      console.log(`🔧 [Row ${rowIndex}] Name correction applied:`, {
        original: value,
        corrected: correctedName,
        hasCorrection,
        storedOriginal: newData[rowIndex]['_original_name']
      });
    }
    
    newData[rowIndex][columnKey] = processedValue;
    
    if (columnKey === 'pic' && !processedValue) {
      newData[rowIndex][columnKey] = currentUserName;
    }
    
    onChange(newData);
    
    // Clear the operation flag - no automatic validation
    setTimeout(() => {
      setOperationsInProgress(prev => {
        const newOperations = new Set(prev);
        newOperations.delete(rowIndex);
        return newOperations;
      });
      
      console.log(`✅ [Row ${rowIndex}] Cell change completed`);
    }, 150);
    
  }, [data, onChange, currentUserName, formatSmartDate, mapDeliverableType, getCorrectedName]);

  // FIXED: Enhanced manual match handler with complete operation isolation
  const handleManualMatchChange = useCallback((rowIndex: number, selectedValue: string) => {
    console.log(`🎯 [Row ${rowIndex}] Manual match starting: selectedValue="${selectedValue}"`);
    
    // Mark this row as having an operation in progress
    setOperationsInProgress(prev => new Set(prev).add(rowIndex));
    
    const newData = [...data];
    
    if (!selectedValue) {
      // Handle clearing the selection - reset to auto-match if available
      const matchResult = matchResults[rowIndex];
      if (matchResult && matchResult.kolDbId) {
        // Restore auto-match
        const kolDbEntry = kolDbData.find(kol => kol.id === matchResult.kolDbId);
        if (kolDbEntry) {
          newData[rowIndex]['matched_creator'] = `${kolDbEntry.creator_name} (${kolDbEntry.social_media_handler || 'No handler'})`;
          newData[rowIndex]['_selectedKolId'] = matchResult.kolDbId;
          newData[rowIndex]['_kolDbId'] = matchResult.kolDbId;
          newData[rowIndex]['_tier'] = matchResult.tier || '';
          newData[rowIndex]['_language'] = matchResult.language || '';
          newData[rowIndex]['_channel'] = matchResult.channel || '';
          newData[rowIndex]['_confidence'] = matchResult.confidence.toString();
          newData[rowIndex]['_manuallyMapped'] = 'false'; // Clear manual mapping flag
        }
      } else {
        // No auto-match available, clear everything
        newData[rowIndex]['matched_creator'] = 'No match found';
        newData[rowIndex]['_selectedKolId'] = '';
        newData[rowIndex]['_kolDbId'] = '';
        newData[rowIndex]['_tier'] = '';
        newData[rowIndex]['_language'] = '';
        newData[rowIndex]['_channel'] = '';
        newData[rowIndex]['_confidence'] = '';
        newData[rowIndex]['_manuallyMapped'] = 'false'; // Clear manual mapping flag
      }
    } else {
      // Handle manual selection
      const kolDbEntry = kolDbData.find(kol => kol.id === selectedValue);
      
      if (kolDbEntry) {
        // Update matched creator display
        newData[rowIndex]['matched_creator'] = `${kolDbEntry.creator_name} (${kolDbEntry.social_media_handler || 'No handler'})`;
        
        // Store the manually selected KOL ID and set manual mapping flag
        newData[rowIndex]['_selectedKolId'] = selectedValue;
        newData[rowIndex]['_kolDbId'] = selectedValue;
        newData[rowIndex]['_tier'] = kolDbEntry.tier || '';
        newData[rowIndex]['_language'] = ''; // This would come from kol_tracker analysis
        newData[rowIndex]['_channel'] = ''; // This would come from post link analysis
        newData[rowIndex]['_confidence'] = '95'; // Manual selection gets high confidence
        newData[rowIndex]['_manuallyMapped'] = 'true'; // Set manual mapping flag
        
        console.log(`🎯 [Row ${rowIndex}] Manual match applied:`, {
          kolDbId: selectedValue,
          creator: kolDbEntry.creator_name,
          tier: kolDbEntry.tier,
          confidence: 95,
          selectedKolId: selectedValue,
          manuallyMapped: true
        });
      }
    }
    
    onChange(newData);
    
    // Call onManualMatch for actual manual matching operations
    if (onManualMatch && selectedValue) {
      onManualMatch(rowIndex, selectedValue, '');
    }
    
    // Clear operation flag - no automatic validation
    setTimeout(() => {
      setOperationsInProgress(prev => {
        const newOperations = new Set(prev);
        newOperations.delete(rowIndex);
        return newOperations;
      });
      
      console.log(`✅ [Row ${rowIndex}] Manual match completed`);
    }, 200);
    
  }, [data, kolDbData, matchResults, onChange, onManualMatch]);

  const handleAddRow = useCallback(() => {
    const newRow: GridRowData = {};
    columns.forEach(col => {
      if (col.key === 'pic') {
        newRow[col.key] = currentUserName;
      } else if (col.key === 'matched_creator') {
        newRow[col.key] = 'No match found';
      } else if (col.key === 'correction_status') {
        newRow[col.key] = '';
      } else {
        newRow[col.key] = '';
      }
    });
    
    // ENHANCED: Add hidden fields for kol_tracker table
    if (selectedTable === 'kol_tracker') {
      newRow['_selectedKolId'] = '';
      newRow['_kolDbId'] = '';
      newRow['_tier'] = '';
      newRow['_language'] = '';
      newRow['_channel'] = '';
      newRow['_confidence'] = '';
      newRow['_original_name'] = '';
      newRow['_manuallyMapped'] = 'false'; // Add manual mapping flag
    }
    
    onChange([...data, newRow]);
  }, [columns, currentUserName, data, onChange, selectedTable]);

  const handleDeleteRow = useCallback((rowIndex: number) => {
    if (onDeleteRow) {
      onDeleteRow(rowIndex);
    }
  }, [onDeleteRow]);

  // ENHANCED: Multi-column paste handling with name correction and tier/language/channel carry-over
  const handlePaste = useCallback(async (e: React.ClipboardEvent, startRowIndex: number, startColumnKey: string) => {
    e.preventDefault();
    setIsPasting(true);

    try {
      const clipboardData = e.clipboardData.getData('text');
      if (!clipboardData) return;

      // Parse clipboard data (handle Excel/CSV style data)
      const rows = clipboardData.split(/\r?\n/).filter(row => row.trim() !== '');
      const startColIndex = columns.findIndex(col => col.key === startColumnKey);
      
      if (startColIndex === -1) return;

      const newData = [...data];
      
      // Ensure we have enough rows
      const requiredRows = startRowIndex + rows.length;
      while (newData.length < requiredRows) {
        const newRow: GridRowData = {};
        columns.forEach(col => {
          if (col.key === 'pic') {
            newRow[col.key] = currentUserName;
          } else if (col.key === 'matched_creator') {
            newRow[col.key] = 'No match found';
          } else if (col.key === 'correction_status') {
            newRow[col.key] = '';
          } else {
            newRow[col.key] = '';
          }
        });
        
        // Add hidden fields for kol_tracker
        if (selectedTable === 'kol_tracker') {
          newRow['_selectedKolId'] = '';
          newRow['_kolDbId'] = '';
          newRow['_tier'] = '';
          newRow['_language'] = '';
          newRow['_channel'] = '';
          newRow['_confidence'] = '';
          newRow['_original_name'] = '';
          newRow['_manuallyMapped'] = 'false';
        }
        
        newData.push(newRow);
      }

      // Process each row of pasted data
      rows.forEach((row, rowOffset) => {
        const cells = row.split('\t'); // Tab-separated values from Excel/Sheets
        const targetRowIndex = startRowIndex + rowOffset;
        
        if (targetRowIndex >= newData.length) return;

        // Fill cells starting from the start column
        cells.forEach((cellValue, colOffset) => {
          const targetColIndex = startColIndex + colOffset;
          if (targetColIndex < columns.length && !columns[targetColIndex].readOnly) {
            const columnKey = columns[targetColIndex].key;
            let processedValue = cellValue.trim();
            
            // Apply smart transformations
            if (columnKey === 'posting_date') {
              processedValue = formatSmartDate(processedValue);
            }
            if (columnKey === 'type') {
              processedValue = mapDeliverableType(processedValue);
            }
            
            // ENHANCED: Apply name correction for name field during paste
            if (columnKey === 'name') {
              const { correctedName, hasCorrection } = getCorrectedName(processedValue);
              // Store the original value if it was corrected
              newData[targetRowIndex]['_original_name'] = hasCorrection ? processedValue : '';
              processedValue = correctedName;
              
              console.log(`🔧 [Row ${targetRowIndex}] Paste name correction:`, {
                original: processedValue,
                corrected: correctedName,
                hasCorrection
              });
            }
            
            newData[targetRowIndex][columnKey] = processedValue;
          }
        });
      });

      onChange(newData);
      
      // Show success feedback
      console.log(`📋 Paste completed: ${rows.length} rows with ${Math.max(...rows.map(r => r.split('\t').length))} columns`);
      
    } catch (error) {
      console.error('❌ Paste operation failed:', error);
    } finally {
      setIsPasting(false);
    }
  }, [data, onChange, columns, currentUserName, formatSmartDate, mapDeliverableType, getCorrectedName, selectedTable]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, rowIndex: number, columnKey: string) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'v') {
        // Paste will be handled by onPaste event
        return;
      }
    }
    
    // Tab navigation
    if (e.key === 'Tab') {
      e.preventDefault();
      const currentColIndex = columns.findIndex(col => col.key === columnKey);
      const nextColIndex = e.shiftKey ? currentColIndex - 1 : currentColIndex + 1;
      
      if (nextColIndex >= 0 && nextColIndex < columns.length) {
        setFocusedCell({ row: rowIndex, col: columns[nextColIndex].key });
      } else if (!e.shiftKey && nextColIndex >= columns.length) {
        if (rowIndex + 1 < data.length) {
          setFocusedCell({ row: rowIndex + 1, col: columns[0].key });
        }
      } else if (e.shiftKey && nextColIndex < 0) {
        if (rowIndex > 0) {
          setFocusedCell({ row: rowIndex - 1, col: columns[columns.length - 1].key });
        }
      }
    }
  }, [columns, data.length]);

  const renderCell = useCallback((rowData: GridRowData, column: Column, rowIndex: number) => {
    const value = rowData[column.key] || '';
    const isFocused = focusedCell?.row === rowIndex && focusedCell?.col === column.key;
    const validation = validationResults[rowIndex];
    const hasError = validation && !validation.isValid;
    const hasWarning = validation && validation.warnings.length > 0;

    // Handle PIC column with access level restrictions
    if (column.key === 'pic') {
      const isEditable = userAccessLevel === 'full-access';
      
      if (isEditable) {
        // For full access users, show editable field
        return (
          <Input
            value={value}
            onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
            onFocus={() => setFocusedCell({ row: rowIndex, col: column.key })}
            onBlur={() => setFocusedCell(null)}
            className={`
              border-0 rounded-none h-full resize-none text-sm
              focus:ring-2 focus:ring-blue-500 focus:border-transparent
              ${isFocused ? 'z-10 relative' : ''}
            `}
            placeholder="Enter PIC name"
          />
        );
      } else {
        // For limited access users, show read-only field
        return (
          <div className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 h-full flex items-center">
            {value || currentUserName}
          </div>
        );
      }
    }

    // ENHANCED: Handle KOL Name column with correction display and tier/language/channel integration
    if (column.key === 'name') {
      const currentName = value;
      const originalName = rowData['_original_name'] || '';
      const hasCorrection = !!originalName;
      
      // Display format: "Original → Corrected" or just the name if no correction
      const displayValue = hasCorrection ? `${originalName} → ${currentName}` : currentName;
      
      const inputElement = (
        <Input
          value={displayValue}
          onChange={(e) => {
            // When user edits, we need to handle the raw input
            const inputValue = e.target.value;
            // Remove the correction format if user is editing
            const cleanValue = inputValue.includes(' → ') ? inputValue.split(' → ').pop() || inputValue : inputValue;
            handleCellChange(rowIndex, column.key, cleanValue);
          }}
          onFocus={() => setFocusedCell({ row: rowIndex, col: column.key })}
          onBlur={() => setFocusedCell(null)}
          onKeyDown={(e) => handleKeyDown(e, rowIndex, column.key)}
          onPaste={(e) => handlePaste(e, rowIndex, column.key)}
          className={`
            border-0 rounded-none h-full resize-none text-sm
            focus:ring-2 focus:ring-blue-500 focus:border-transparent
            ${hasError ? 'bg-red-50 dark:bg-red-900/20 border-red-200' : ''}
            ${hasWarning ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200' : ''}
            ${hasCorrection ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200' : ''}
            ${isFocused ? 'z-10 relative' : ''}
            ${isPasting && isFocused ? 'bg-blue-50 border-blue-300' : ''}
          `}
          placeholder={column.required ? `${column.label} (required)` : column.label}
          autoFocus={isFocused}
        />
      );

      return inputElement;
    }

    // ENHANCED: Handle matched_creator column with ULTRA HIGH z-index and proper auto-match display
    if (column.key === 'matched_creator') {
      const matchResult = matchResults[rowIndex];
      const kolOptions = kolDbData.map(kol => ({
        label: `${kol.creator_name} (${kol.social_media_handler || 'No handler'})`,
        value: kol.id
      }));

      // FIXED: Determine the correct selected value
      let selectedKolId = rowData['_selectedKolId'] || '';
      
      // If no manual selection but we have an auto-match, use the auto-match
      if (!selectedKolId && matchResult && matchResult.kolDbId) {
        selectedKolId = matchResult.kolDbId;
        console.log(`🔄 [Row ${rowIndex}] Using auto-match ID: ${selectedKolId}`);
      }

      return (
        <div className="px-2 py-1 h-full flex items-center gap-2 relative">
          <div className="flex-1 relative">
            <SearchableSelect
              options={kolOptions}
              value={selectedKolId}
              onChange={(selectedValue) => handleManualMatchChange(rowIndex, selectedValue)}
              placeholder="Select creator..."
              className={`w-full ${isFocused ? 'z-[99999] relative' : 'z-[9998]'}`}
              badgeVariant="blue"
              clearable={true}
            />
          </div>
          {matchResult && matchResult.confidence > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge 
                    variant="outline" 
                    className={`
                      text-sm px-2 py-1 border font-bold whitespace-nowrap
                      ${matchResult.confidence >= 70 ? 'border-green-500 text-green-700 bg-green-50' : ''}
                      ${matchResult.confidence >= 50 && matchResult.confidence < 70 ? 'border-yellow-500 text-yellow-700 bg-yellow-50' : ''}
                      ${matchResult.confidence < 50 ? 'border-red-500 text-red-700 bg-red-50' : ''}
                    `}
                  >
                    {matchResult.confidence.toFixed(0)}%
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-gray-800 text-white max-w-xs">
                  <div className="space-y-1">
                    <div><strong>Confidence:</strong> {matchResult.confidence.toFixed(1)}%</div>
                    <div><strong>Reason:</strong> {matchResult.matchReason}</div>
                    {matchResult.tier && <div><strong>Tier:</strong> {matchResult.tier}</div>}
                    {matchResult.language && <div><strong>Language:</strong> {matchResult.language}</div>}
                    {matchResult.channel && <div><strong>Channel:</strong> {matchResult.channel}</div>}
                    {matchResult.warnings.length > 0 && (
                      <div>
                        <strong>Warnings:</strong>
                        <ul className="list-disc list-inside mt-1">
                          {matchResult.warnings.map((warning, index) => (
                            <li key={index} className="text-yellow-200">{warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      );
    }

    if (column.readOnly && column.key !== 'matched_creator') {
      return (
        <div className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 h-full flex items-center">
          {value || 'Auto-generated'}
        </div>
      );
    }

    const inputElement = (
      <Input
        value={value}
        onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
        onFocus={() => setFocusedCell({ row: rowIndex, col: column.key })}
        onBlur={() => setFocusedCell(null)}
        onKeyDown={(e) => handleKeyDown(e, rowIndex, column.key)}
        onPaste={(e) => handlePaste(e, rowIndex, column.key)}
        className={`
          border-0 rounded-none h-full resize-none text-sm
          focus:ring-2 focus:ring-blue-500 focus:border-transparent
          ${hasError ? 'bg-red-50 dark:bg-red-900/20 border-red-200' : ''}
          ${hasWarning ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200' : ''}
          ${isFocused ? 'z-10 relative' : ''}
          ${isPasting && isFocused ? 'bg-blue-50 border-blue-300' : ''}
        `}
        placeholder={column.required ? `${column.label} (required)` : column.label}
        autoFocus={isFocused}
      />
    );

    // Show tooltip for validation errors
    if (hasError && validation?.errors.length > 0) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {inputElement}
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-red-600 text-white">
              {validation.errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return inputElement;
  }, [focusedCell, validationResults, matchResults, handleCellChange, handleKeyDown, handlePaste, isPasting, kolDbData, handleManualMatchChange, userAccessLevel, currentUserName]);

  // ENHANCED: Validation indicator with manual mapping status and comprehensive confidence scoring - MADE MORE COMPACT
  const renderValidationIndicator = useCallback((rowIndex: number) => {
    const validation = validationResults[rowIndex];
    const rowData = data[rowIndex] || {};
    const originalName = rowData['_original_name'] || '';
    const hasCorrection = !!originalName;
    const matchResult = matchResults[rowIndex];
    const isManuallyMapped = rowData._manuallyMapped === 'true';
    
    console.log(`🎯 [Row ${rowIndex}] Validation Indicator - Validation:`, validation, 'Match Result:', matchResult, 'Selected Table:', selectedTable, 'Manually Mapped:', isManuallyMapped);
    
    // Check if row has meaningful data
    const hasData = Object.values(rowData).some(value => value.trim() !== '');
    if (!hasData) return null;

    // PRIORITY 1: Show "Manual Mapped" status for manually selected creators - MORE COMPACT
    if (selectedTable === 'kol_tracker' && isManuallyMapped && hasData) {
      return (
        <div className="flex items-center justify-center h-full px-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center gap-0.5 cursor-help">
                  <Badge 
                    variant="outline" 
                    className="text-xs px-1.5 py-0.5 border font-bold border-blue-600 text-blue-800 bg-blue-100"
                  >
                    Manual
                  </Badge>
                  <span className="text-xs text-blue-600 font-medium">Mapped</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-blue-600 text-white max-w-xs">
                <div className="space-y-1">
                  <div><strong>Status:</strong> Manually Mapped</div>
                  <div><strong>Creator:</strong> {rowData.matched_creator}</div>
                  <div>This creator was manually selected and is ready for import.</div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }

    // PRIORITY 2: Show confidence score for kol_tracker table with auto-match results - MORE COMPACT
    if (selectedTable === 'kol_tracker' && matchResult && hasData && !isManuallyMapped) {
      const confidenceInfo = getConfidenceLevel(matchResult.confidence);
      
      console.log(`🏆 [Row ${rowIndex}] Showing confidence badge:`, matchResult.confidence, confidenceInfo);
      
      return (
        <div className="flex items-center justify-center h-full px-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center gap-0.5 cursor-help">
                  <Badge 
                    variant="outline" 
                    className={`
                      text-xs px-1.5 py-0.5 border font-bold
                      ${confidenceInfo.color === 'green' ? 'border-green-500 text-green-700 bg-green-50' : ''}
                      ${confidenceInfo.color === 'blue' ? 'border-blue-500 text-blue-700 bg-blue-50' : ''}
                      ${confidenceInfo.color === 'yellow' ? 'border-yellow-500 text-yellow-700 bg-yellow-50' : ''}
                      ${confidenceInfo.color === 'orange' ? 'border-orange-500 text-orange-700 bg-orange-50' : ''}
                      ${confidenceInfo.color === 'red' ? 'border-red-500 text-red-700 bg-red-50' : ''}
                    `}
                  >
                    {matchResult.confidence.toFixed(0)}%
                  </Badge>
                  <span className="text-xs text-gray-500">{confidenceInfo.label}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-gray-800 text-white max-w-xs">
                <div className="space-y-1">
                  <div><strong>Match Confidence:</strong> {matchResult.confidence.toFixed(1)}%</div>
                  <div><strong>Reason:</strong> {matchResult.matchReason}</div>
                  {matchResult.tier && <div><strong>Tier:</strong> {matchResult.tier}</div>}
                  {matchResult.language && <div><strong>Language:</strong> {matchResult.language}</div>}
                  {matchResult.channel && <div><strong>Channel:</strong> {matchResult.channel}</div>}
                  {matchResult.warnings.length > 0 && (
                    <div>
                      <strong>Warnings:</strong>
                      <ul className="list-disc list-inside mt-1">
                        {matchResult.warnings.map((warning, index) => (
                          <li key={index} className="text-yellow-200">{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }

    // PRIORITY 3: Show correction indicator if name was corrected - MORE COMPACT
    if (hasCorrection) {
      const correctedName = rowData['name'] || '';
      
      return (
        <div className="flex items-center justify-center h-full px-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-help">
                  <AlertTriangle className="h-3 w-3 text-blue-600" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-blue-600 text-white max-w-xs">
                KOL name was auto-corrected: {originalName} → {correctedName}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }

    // PRIORITY 4: Show validation status (errors/warnings) - MORE COMPACT
    if (validation) {
      return (
        <div className="flex items-center justify-center h-full px-1">
          {validation.isValid ? (
            <CheckCircle className="h-3 w-3 text-green-600" />
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-help">
                    <AlertCircle className="h-3 w-3 text-red-600" />
                    {validation.warnings.length > 0 && (
                      <AlertCircle className="h-3 w-3 text-yellow-600" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="bg-red-600 text-white max-w-xs">
                  {validation.errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                  {validation.warnings.map((warning, index) => (
                    <div key={`warning-${index}`} className="text-yellow-200">{warning}</div>
                  ))}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      );
    }

    return null;
  }, [validationResults, data, matchResults, selectedTable]);

  const renderSortIcon = useCallback((columnKey: string) => {
    if (sortConfig?.key === columnKey) {
      return sortConfig.direction === 'asc' 
        ? <ArrowUp className="h-3 w-3 ml-1" />
        : <ArrowDown className="h-3 w-3 ml-1" />;
    }
    return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
  }, [sortConfig]);

  // Calculate total grid width for horizontal scrolling - UPDATED for cleaner layout
  const totalGridWidth = useMemo(() => {
    const deleteRowWidth = 80;
    const statusWidth = 100; // Reduced from 120
    const actualColumns = columns.filter(col => col.key !== 'actions' && col.key !== 'correction_status');
    const columnsWidth = actualColumns.reduce((total, col) => total + (columnWidths[col.key] || 150), 0);
    return deleteRowWidth + statusWidth + columnsWidth;
  }, [columns, columnWidths]);

  // UPDATED: Grid template columns calculation for cleaner layout
  const gridTemplateColumns = useMemo(() => {
    const deleteRowWidth = '80px';
    const statusWidth = '100px'; // Reduced from 120px
    const actualColumns = columns.filter(col => col.key !== 'actions' && col.key !== 'correction_status');
    const columnWidthsStr = actualColumns.map(col => `${columnWidths[col.key] || 150}px`).join(' ');
    return `${deleteRowWidth} ${statusWidth} ${columnWidthsStr}`;
  }, [columns, columnWidths]);

  // Filter out action columns for rendering
  const actualColumns = columns.filter(col => col.key !== 'actions' && col.key !== 'correction_status');

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header - Fixed with synchronized horizontal scroll */}
      <div className="flex-shrink-0 border-b bg-gray-50 dark:bg-gray-800">
        <div 
          ref={headerScrollRef}
          className="overflow-x-auto"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div 
            className="grid"
            style={{ 
              gridTemplateColumns,
              minWidth: `${totalGridWidth}px`
            }}
          >
            {/* Delete/Add Row Column */}
            <div className="p-3 border-r font-medium text-sm text-center flex items-center justify-center bg-gray-50 dark:bg-gray-800 sticky left-0 z-30">
              <Button
                onClick={handleAddRow}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                title="Add new row"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Status Column - MORE COMPACT */}
            <div className="p-1.5 border-r font-medium text-xs text-center flex items-center justify-center bg-gray-50 dark:bg-gray-800">
              Status
            </div>
            
            {/* Data Columns with Sorting */}
            {actualColumns.map((column) => (
              <div
                key={column.key}
                className={`p-3 border-r font-medium text-sm text-left flex items-center ${
                  column.key !== 'correction_status'
                    ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' 
                    : ''
                }`}
                onClick={() => handleSort(column.key)}
              >
                <span className="flex items-center">
                  {column.label}
                  {column.required && <span className="text-red-500 ml-1">*</span>}
                  {renderSortIcon(column.key)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data Grid with synchronized horizontal scroll */}
      <div 
        ref={bodyScrollRef}
        className="flex-1 overflow-auto"
      >
        <div 
          className="grid auto-rows-fr"
          style={{ 
            gridTemplateColumns,
            minWidth: `${totalGridWidth}px`
          }}
        >
          {sortedData.map((rowData, rowIndex) => (
            <React.Fragment key={`row-${rowIndex}`}>
              {/* Delete Row Column */}
              <div className="border-r border-b flex items-center justify-center text-sm bg-gray-50 dark:bg-gray-800 sticky left-0 z-10 min-h-[52px]">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteRow(rowIndex)}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                  title="Delete this row"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Status Column - MORE COMPACT */}
              <div className="border-r border-b flex items-center justify-center min-h-[52px] p-1">
                {renderValidationIndicator(rowIndex)}
              </div>
              
              {/* Data Columns */}
              {actualColumns.map((column) => (
                <div 
                  key={`${rowIndex}-${column.key}`} 
                  className="border-r border-b flex items-center hover:bg-gray-50 dark:hover:bg-gray-800/50 min-h-[52px]"
                >
                  {renderCell(rowData, column, rowIndex)}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
        
        {/* Empty state */}
        {data.length === 0 && (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <p className="text-sm">No data rows yet</p>
              <Button
                onClick={handleAddRow}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Row
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t bg-gray-50 dark:bg-gray-800 p-3">
        <div className="flex items-center justify-between text-sm flex-wrap gap-2">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-gray-600 dark:text-gray-400">
              Total Rows: {data.length}
            </span>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Valid: {Object.values(validationResults).filter(v => v.isValid).length}</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span>Invalid: {Object.values(validationResults).filter(v => !v.isValid).length}</span>
            </div>
            {selectedTable === 'kol_tracker' && Object.keys(matchResults).length > 0 && (
              <>
                <div className="flex items-center gap-2 text-blue-600">
                  <span>Auto Matches: {Object.values(matchResults).filter(r => r.kolDbId).length}</span>
                </div>
                <div className="flex items-center gap-2 text-blue-800">
                  <span>Manual: {data.filter(row => row._manuallyMapped === 'true').length}</span>
                </div>
                <div className="flex items-center gap-2 text-blue-600">
                  <span>Avg Confidence: {(Object.values(matchResults).reduce((sum, result) => sum + result.confidence, 0) / Object.values(matchResults).length).toFixed(0)}%</span>
                </div>
                <div className="flex items-center gap-2 text-green-600">
                  <span>With Tier: {Object.values(matchResults).filter(r => r.tier).length}</span>
                </div>
              </>
            )}
            {sortConfig && (
              <div className="flex items-center gap-2 text-blue-600">
                <ArrowUpDown className="h-4 w-4" />
                <span>Sorted by {actualColumns.find(c => c.key === sortConfig.key)?.label}</span>
              </div>
            )}
          </div>
          <div className="text-sm text-gray-500">
            Multiple PICs: Use comma separation (e.g., "John, Jane, Mike") • Ctrl+V for multi-column paste • Tab/Shift+Tab navigation • Click headers to sort
          </div>
        </div>
      </div>
    </div>
  );
}
